import { memo } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { formatCurrency } from "@/shared/lib/formatters";
import { RATE_TYPE_LABELS } from "./CommissionTypeBadge";
import type { CommissionResponseDTO } from "../types/commission.types";

interface CommissionDeleteDialogProps {
  open: boolean;
  loading: boolean;
  commission: CommissionResponseDTO | null;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Confirmação de exclusão de uma comissão. O backend faz soft delete —
 * o registro deixa de aparecer nas listagens.
 *
 * Exibe id, tipo e valor para evitar exclusão acidental.
 *
 * @example
 * <CommissionDeleteDialog
 *   open={dialogMode === 'delete'}
 *   loading={dialogLoading}
 *   commission={selected}
 *   onClose={closeDialog}
 *   onConfirm={handleDeleteConfirm}
 * />
 */
export const CommissionDeleteDialog = memo(function CommissionDeleteDialog({
  open,
  loading,
  commission,
  onClose,
  onConfirm,
}: CommissionDeleteDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Excluir Comissão</DialogTitle>
          <DialogDescription>
            Tem certeza que deseja excluir esta comissão?
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="p-4 bg-destructive/10 rounded-md border border-destructive/20">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Comissão #{commission?.id}</p>
                {commission ? (
                  <p className="text-sm text-muted-foreground">
                    Tipo: {RATE_TYPE_LABELS[commission.rateType]}
                  </p>
                ) : null}
                {commission ? (
                  <p className="text-sm text-muted-foreground">
                    Valor: {formatCurrency(commission.value)}
                  </p>
                ) : null}
                <p className="text-sm text-destructive mt-2">
                  A comissão deixará de aparecer nas listagens.
                </p>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading}>
            {loading ? "Excluindo..." : "Excluir Comissão"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
